import {
  CopyOutlined,
  DeleteOutlined,
  ClearOutlined,
  PlusSquareOutlined,
  SearchOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Checkbox,
  ConfigProvider,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Popover,
  Select,
  Tag,
  Typography,
} from "antd";
import _ from "lodash";
import {
  CSSProperties,
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import AdvancedTable, {
  AddButton,
  AdvancedTableRef,
  getActionColumns,
  getIdColumn,
} from "../components/AdvancedTable";
import { useFeedback } from "../contexts/FeedbackProvider";
import {
  KeyValueDataProvider,
  useKeyValueData,
} from "../contexts/KeyValueDataProvider";
import { PropsDataProvider, usePropsData } from "../contexts/PropsDataProvider";
import { Filter } from "../shared/protocols/editor/PtlDataFind";
import {
  Chance,
  Count,
  Entry,
  getDescription,
  Indexes,
  Location,
  Props,
  Scope,
  Target,
  TargetSelect,
  Times,
  Trigger,
  WeightChange,
  YieldChange,
} from "../shared/types/Props";
import { UpdateData } from "../shared/protocols/editor/PtlDataUpdate";
import { dataCall } from "../tsrpc";
import PageKeyValue from "./PageKeyValue";
import PagePropRarityWight from "./PagePropRarityWight";

const { Title } = Typography;
const PROP_NAME_REF_LIMIT = 10000;
const STOCK_PROP_TYPE = 1;
type PropNameRef = Pick<Props, "_id" | "name" | "type"> &
  Partial<Pick<Props, "assetId" | "rarity" | "tags" | "yield" | "derived">>;
type PropInfo = Partial<Props> & Pick<Props, "_id" | "name">;
type PropSelectorFilter = { keyword: string; tagIds: number[] };
const PropNameRefsContext = createContext<PropNameRef[]>([]);
const PropSelectorFilterContext = createContext<{
  filter: PropSelectorFilter;
  setFilter: (filter: PropSelectorFilter) => void;
}>({
  filter: { keyword: "", tagIds: [] },
  setFilter: () => undefined,
});

export default function PageProps() {
  const navigate = useNavigate();

  return (
    <KeyValueDataProvider>
      <Routes>
        <Route
          index
          element={
            <PropsDataProvider>
              <PagePropList />
            </PropsDataProvider>
          }
        />
        <Route
          path="type"
          element={
            <PageKeyValue
              title="道具类型管理"
              collection="proptypes"
              backText="返回道具列表"
              onBack={() => navigate("/control/props")}
            />
          }
        />
        <Route
          path="rarity"
          element={
            <PageKeyValue
              title="道具品质管理"
              collection="proprarities"
              backText="返回道具列表"
              onBack={() => navigate("/control/props")}
            />
          }
        />
        <Route
          path="tag"
          element={
            <PageKeyValue
              title="道具标签管理"
              collection="proptags"
              backText="返回道具列表"
              onBack={() => navigate("/control/props")}
            />
          }
        />
        <Route path="weight" element={<PagePropRarityWight />} />
      </Routes>
    </KeyValueDataProvider>
  );
}

function PagePropList() {
  const navigate = useNavigate();
  const tableRef = useRef<AdvancedTableRef<Props>>(null);
  const context = usePropsData();
  const { message } = useFeedback()!;

  const [editingData, setEditingData] = useState<Props>();
  const [editingEntryDesc, setEditingEntryDesc] = useState<
    string | undefined
  >();
  const { data: types, load: loadTypes } = useKeyValueData("proptypes");
  const { data: rarities, load: loadRarities } =
    useKeyValueData("proprarities");
  const { data: tags, load: loadTags } = useKeyValueData("proptags");
  const [version, setVersion] = useState(0);
  const [propNameRefs, setPropNameRefs] = useState<PropNameRef[]>([]);
  const [propSelectorFilter, setPropSelectorFilter] =
    useState<PropSelectorFilter>({ keyword: "", tagIds: [] });

  const [filter, setFilter] = useState<Filter>({});
  const [search, setSearch] = useState<Record<string, string | undefined>>({});

  const loadPropNameRefs = useCallback(async () => {
    const data = await context.get(
      {},
      { _id: 1 },
      PROP_NAME_REF_LIMIT,
      0,
      {
        _id: 1,
        name: 1,
        type: 1,
        assetId: 1,
        rarity: 1,
        tags: 1,
        yield: 1,
        derived: 1,
      },
    );
    setPropNameRefs(data as PropNameRef[]);
  }, [context]);

  const descriptionProps = useMemo(() => {
    const map = new Map<string, PropNameRef>();
    for (const prop of propNameRefs) map.set(prop._id.toString(), prop);
    for (const prop of context.data) map.set(prop._id.toString(), prop);
    if (editingData) map.set(editingData._id.toString(), editingData);
    return Array.from(map.values()) as Props[];
  }, [context.data, editingData, propNameRefs]);

  const propInfoMap = useMemo(() => {
    const map = new Map<string, PropInfo>();
    for (const prop of descriptionProps) {
      if (prop?._id && prop.name) map.set(prop._id.toString(), prop);
    }
    return map;
  }, [descriptionProps]);

  useEffect(() => {
    if (!types || types.length === 0) loadTypes();
    if (!rarities || rarities.length === 0) loadRarities();
    if (!tags || tags.length === 0) loadTags();
    getVersion();
    loadPropNameRefs();
  }, []);

  useEffect(() => {
    setEditingEntryDesc(
      editingData
        ? getDescription(editingData, descriptionProps, tags, rarities, true)
        : undefined,
    );
  }, [descriptionProps, editingData, rarities, tags]);

  const getVersion = async () => {
    const res = await dataCall("editor/DataFind", {
      collection: "versions",
      filter: { collection: "props" },
      limit: 1,
    });
    setVersion(res.res?.data?.[0]?.version ?? 0);
  };

  const parseInlineStyle = (styleText?: string | null): CSSProperties => {
    const style: CSSProperties = {};
    styleText
      ?.split(";")
      .map((item) => item.trim())
      .filter(Boolean)
      .forEach((item) => {
        const [rawKey, ...rawValue] = item.split(":");
        const value = rawValue.join(":").trim();
        switch (rawKey.trim()) {
          case "color":
            style.color = value;
            break;
          case "cursor":
            style.cursor = value;
            break;
          case "border-bottom":
            style.borderBottom = value;
            break;
        }
      });
    return style;
  };

  const PropInfoRow = ({
    label,
    value,
  }: {
    label: string;
    value?: ReactNode;
  }) => {
    if (value === undefined || value === null || value === "") return null;
    return (
      <Flex gap={12} justify="space-between" align="flex-start">
        <span style={{ color: "#888", whiteSpace: "nowrap" }}>{label}</span>
        <span style={{ wordBreak: "break-all", textAlign: "right" }}>
          {value}
        </span>
      </Flex>
    );
  };

  const renderPropInfoCard = (
    prop: PropInfo | undefined,
    propId: string,
    fallbackName: string,
  ) => {
    const entryDesc =
      prop?.entries?.length && prop.type !== undefined
        ? getDescription(prop as Props, descriptionProps, tags, rarities, false)
        : undefined;
    return (
      <Card
        size="small"
        bordered={false}
        title={prop?.name ?? fallbackName ?? propId}
        style={{ width: 320, maxWidth: "calc(100vw - 32px)" }}
      >
        <Flex vertical gap={6}>
          <PropInfoRow label="ID" value={prop?._id ?? propId} />
          <PropInfoRow label="资源" value={prop?.assetId} />
          <PropInfoRow
            label="类型"
            value={
              prop?.type === undefined
                ? undefined
                : types.find((item) => item.value === prop.type)?.label ??
                  prop.type
            }
          />
          <PropInfoRow
            label="品质"
            value={
              prop?.rarity === undefined
                ? undefined
                : rarities.find((item) => item.value === prop.rarity)?.label ??
                  prop.rarity
            }
          />
          {Boolean(prop?.tags?.length) && (
            <PropInfoRow
              label="标签"
              value={
                <Flex gap={4} wrap="wrap" justify="flex-end">
                  {prop?.tags?.map((tag) => (
                    <Tag key={tag} style={{ marginInlineEnd: 0 }}>
                      {tags.find((item) => item.value === tag)?.label ?? tag}
                    </Tag>
                  ))}
                </Flex>
              }
            />
          )}
          <PropInfoRow
            label="魔力产出"
            value={prop?.yield === undefined ? undefined : prop.yield}
          />
          {prop?.derived && <PropInfoRow label="衍生物" value="是" />}
          {entryDesc && <PropInfoRow label="词条" value={entryDesc} />}
        </Flex>
      </Card>
    );
  };

  const renderDescriptionNode = (node: ChildNode, key: string): ReactNode => {
    if (node.nodeType === 3) return node.textContent;
    if (node.nodeType !== 1) return null;

    const element = node as HTMLElement;
    const children = Array.from(element.childNodes).map((child, index) =>
      renderDescriptionNode(child, `${key}-${index}`),
    );
    const style = parseInlineStyle(element.getAttribute("style"));
    const propId = element.getAttribute("data-prop-id");
    if (propId) {
      return (
        <Popover
          key={key}
          placement="top"
          mouseEnterDelay={0.2}
          content={renderPropInfoCard(
            propInfoMap.get(propId),
            propId,
            element.textContent ?? "",
          )}
        >
          <span
            style={{
              ...style,
              cursor: "help",
              borderBottom: "1px dotted currentColor",
            }}
          >
            {children}
          </span>
        </Popover>
      );
    }
    return (
      <span key={key} style={style}>
        {children}
      </span>
    );
  };

  const renderDescriptionHtml = (html: string, keyPrefix: string) => {
    if (!html) return null;
    if (typeof DOMParser === "undefined") {
      return <span dangerouslySetInnerHTML={{ __html: html }} />;
    }
    const doc = new DOMParser().parseFromString(html, "text/html");
    return Array.from(doc.body.childNodes).map((node, index) =>
      renderDescriptionNode(node, `${keyPrefix}-${index}`),
    );
  };

  const DescText = ({ text }: { text?: string }) => {
    return (
      <>
        {text?.split(/[[\]]/).map((v, i) => (
          <span key={i} style={{ color: i % 2 ? "#f00" : undefined }}>
            {renderDescriptionHtml(v, `desc-${i}`)}
          </span>
        ))}
      </>
    );
  };

  const debounceUpdate = useMemo(() => {
    let timer: NodeJS.Timeout | null = null;
    return (data?: Props) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        setEditingEntryDesc(
          data
            ? getDescription(data, descriptionProps, tags, rarities, true)
            : undefined,
        );
      }, 100);
    };
  }, [descriptionProps, tags, rarities]);

  const onChange = (_: Props, data: Props) => {
    debounceUpdate(data);
  };

  const getAssetId = (alias: string = "Empty"): string => {
    const filtered = context.data.filter((d) => d.assetId?.startsWith(alias));
    const indexes = filtered.map((d) =>
      Number(d.assetId.replace(`${alias}_`, "")),
    );
    return `${alias}_${((_.max(indexes) ?? 0) + 1).toString().padStart(3, "0")}`;
  };

  const sanitizePropsForSubmit = (value: Partial<Props>): Partial<Props> => {
    const baseData = {
      ...(editingData ?? {}),
      ...value,
    } as Props;
    const sanitized = sanitizePropsData(baseData);
    const { _id, ...rest } = sanitized;
    return value._id === undefined ? rest : sanitized;
  };

  const sanitizedContext = useMemo(
    () => ({
      ...context,
      add: async (
        values: Partial<Omit<Props, "_id">> | Partial<Omit<Props, "_id">>[],
      ) => {
        const result = await context.add(
          Array.isArray(values)
            ? values.map(
                (item) =>
                  sanitizePropsForSubmit(item) as Partial<Omit<Props, "_id">>,
              )
            : (sanitizePropsForSubmit(values) as Partial<Omit<Props, "_id">>),
        );
        await loadPropNameRefs();
        return result;
      },
      update: async (filter: Filter, update: UpdateData, multi?: boolean) => {
        if (multi || !("$set" in update) || !update.$set) {
          return context.update(filter, update, multi);
        }
        const result = await context.update(
          filter,
          {
            ...update,
            $set: sanitizePropsForSubmit(update.$set as Partial<Props>) as Record<
              string,
              any
            >,
          },
          multi,
        );
        if (
          (update.$set as Partial<Props>).name !== undefined ||
          (update.$set as Partial<Props>).type !== undefined ||
          (update.$set as Partial<Props>).assetId !== undefined ||
          (update.$set as Partial<Props>).tags !== undefined
        ) {
          await loadPropNameRefs();
        }
        return result;
      },
    }),
    [context, editingData, loadPropNameRefs],
  );

  const propSelectorFilterValue = useMemo(
    () => ({
      filter: propSelectorFilter,
      setFilter: setPropSelectorFilter,
    }),
    [propSelectorFilter],
  );

  const cleanAllRedundantFields = async () => {
    try {
      const allProps = await context.get({}, undefined, 10000);
      const changedProps = allProps
        .map((item) => ({
          original: item,
          sanitized: sanitizePropsData(item),
        }))
        .filter(({ original, sanitized }) => !_.isEqual(original, sanitized));

      if (changedProps.length === 0) {
        message.success("没有检测到需要清理的冗余字段");
        return;
      }

      for (const { original, sanitized } of changedProps) {
        const { _id, ...updateData } = sanitized;
        await context.update(
          { _id: original._id },
          { $set: updateData as Record<string, any> },
        );
      }

      await context.load(filter, true);
      message.success(`已清理 ${changedProps.length} 条道具数据的冗余字段`);
    } catch (e: any) {
      message.error(`清理失败：${e?.message ?? "未知错误"}`);
    }
  };

  return (
    <PropNameRefsContext.Provider value={propNameRefs}>
      <PropSelectorFilterContext.Provider value={propSelectorFilterValue}>
      <AdvancedTable<Props>
      ref={tableRef}
      context={sanitizedContext}
      editingData={editingData}
      onEditingDataChange={onChange}
      filter={filter}
      title={() => (
        <Flex justify="space-between" align="center">
          <Flex gap={10}>
            <Title level={5}>炼金道具管理</Title>
            <p>{`[数据版本：${version}]`}</p>
          </Flex>
          <Flex gap={10}>
            <AddButton<Props>
              title="新增"
              add={() => sanitizedContext.add}
              createValue={() => ({
                type: 1,
                rarity: 1,
                yield: 0,
                assetId: getAssetId(),
              })}
              onValuesChange={(changed, all, form) => {
                if (!changed.tags) return;
                const tag = changed.tags?.[0];
                const alias =
                  (tag && tags.find((t) => t.value === tag)?.alias) || "Empty";
                form.setFieldValue("assetId", getAssetId(alias));
              }}
            >
              <Form.Item
                name="name"
                label="名称"
                rules={[{ required: true, message: "请输入名称！" }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="type"
                label="类型"
                rules={[{ required: true, message: "请输入名称！" }]}
              >
                <Select options={types} />
              </Form.Item>
              <Form.Item name="rarity" label="品质">
                <Select options={rarities} />
              </Form.Item>
              <Form.Item name="tags" label="标签">
                <Select mode="multiple" options={tags} />
              </Form.Item>
              <Form.Item
                name="assetId"
                label="资源ID"
                rules={[{ required: true, message: "请输入资源ID！" }]}
                dependencies={["tags"]}
              >
                <Input />
              </Form.Item>
              <Form.Item name="yield" label="每回合产出">
                <InputNumber />
              </Form.Item>
            </AddButton>
            <Button icon={<SettingOutlined />} onClick={() => navigate("type")}>
              类型管理
            </Button>
            <Button
              icon={<SettingOutlined />}
              onClick={() => navigate("rarity")}
            >
              品质管理
            </Button>
            <Button icon={<SettingOutlined />} onClick={() => navigate("tag")}>
              标签管理
            </Button>
            <Button
              icon={<SettingOutlined />}
              onClick={() => navigate("weight")}
            >
              权重管理
            </Button>
            <Button
              icon={<ClearOutlined />}
              onClick={cleanAllRedundantFields}
            >
              清理冗余字段
            </Button>
          </Flex>
        </Flex>
      )}
      columns={[
        getIdColumn(),
        {
          title: "ID",
          dataIndex: "_id",
          width: 100,
          render: (v) => (
            <Flex>
              <Popover content={v}>
                <p
                  style={{
                    color: "#999",
                    width: "70px",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}
                >
                  {v}
                </p>
              </Popover>
              <CopyOutlined
                onClick={async () => {
                  await navigator.clipboard.writeText(v);
                  message.success("已复制");
                }}
              />
            </Flex>
          ),
        },
        {
          title: "资源ID",
          dataIndex: "assetId",
          width: 120,
          sorter: (a: Props, b: Props) => a.assetId.localeCompare(b.assetId),
          render: (v) => (
            <p
              style={{
                color:
                  context.data.filter((d) => d.assetId === v).length > 1
                    ? "#f00"
                    : undefined,
              }}
            >
              {v}
            </p>
          ),
          editor: <Input />,
          formItemProps: {
            rules: [{ required: true, message: "请输入资源ID！" }],
          },
        },
        {
          title: "名称",
          dataIndex: "name",
          width: 100,
          render: (v) => (
            <p
              style={{
                color:
                  context.data.filter((d) => d.name === v).length > 1
                    ? "#f00"
                    : undefined,
              }}
            >
              {v}
            </p>
          ),
          editor: <Input />,
          filterDropdown: ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
            close,
          }) => (
            <Card styles={{ body: { padding: 8 } }}>
              <Flex vertical gap={8}>
                <Input
                  value={search.name}
                  onChange={(e) => {
                    if (e.target.value)
                      setSearch((prev) => ({ ...prev, name: e.target.value }));
                    else
                      setSearch((prev) => {
                        const { name, ...rest } = prev;
                        return rest;
                      });
                  }}
                  onPressEnter={() => {
                    if (search.name)
                      setFilter((prev) => ({
                        ...prev,
                        name: { $regex: search.name as string },
                      }));
                    else
                      setFilter((prev) => {
                        const { name, ...rest } = prev;
                        return rest;
                      });
                    close();
                  }}
                />
                <Flex gap={8}>
                  <Button
                    block
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={() => {
                      if (search.name)
                        setFilter((prev) => ({
                          ...prev,
                          name: { $regex: search.name as string },
                        }));
                      else
                        setFilter((prev) => {
                          const { name, ...rest } = prev;
                          return rest;
                        });
                      close();
                    }}
                  >
                    搜索
                  </Button>
                  <Button
                    onClick={() => {
                      setSearch((prev) => {
                        const { name, ...rest } = prev;
                        return rest;
                      });
                      setFilter((prev) => {
                        const { name, ...rest } = prev;
                        return rest;
                      });
                      close();
                    }}
                  >
                    重置
                  </Button>
                  <Button onClick={() => close()}>取消</Button>
                </Flex>
              </Flex>
            </Card>
          ),
          filterIcon: <SearchOutlined />,
          formItemProps: {
            rules: [{ required: true, message: "请输入名称！" }],
          },
        },
        {
          title: "类型",
          dataIndex: "type",
          width: 120,
          filters: types.map((t) => ({ text: t.label, value: t.value })),
          onFilter: (value, record) => record.type === value,
          editor: <Select options={types} />,
          render: (v) => types.find((item) => item.value === v)?.label,
          formItemProps: {
            rules: [{ required: true, message: "请输入类型！" }],
          },
        },
        {
          title: "品质",
          dataIndex: "rarity",
          width: 120,
          filters: rarities.map((t) => ({ text: t.label, value: t.value })),
          onFilter: (value, record) => record.rarity === value,
          render: (v) => rarities.find((item) => item.value === v)?.label,
          editor: <Select options={rarities} />,
        },
        {
          title: "标签",
          dataIndex: "tags",
          width: 150,
          filters: tags.map((t) => ({ text: t.label, value: t.value })),
          onFilter: (value, record) =>
            record.tags?.includes(value as number) ?? false,
          render: (v) =>
            v?.map((t: number) => (
              <Tag key={t}>{tags.find((item) => item.value === t)?.label}</Tag>
            )),
          editor: <Select mode="multiple" options={tags} />,
        },
        {
          title: "衍生物",
          dataIndex: "derived",
          width: 100,
          render: (v) => <Checkbox checked={v} />,
          editor: <Checkbox />,
          formItemProps: { valuePropName: "checked" },
        },
        {
          title: "每回合产出",
          dataIndex: "yield",
          width: 100,
          editor: <InputNumber />,
        },
        {
          title: (
            <Flex gap={10} align="center">
              <p style={{ width: 40 }}>词条</p>
              {editingData && (
                <p style={{ flex: 1, fontWeight: "normal" }}>
                  <DescText text={editingEntryDesc} />
                </p>
              )}
            </Flex>
          ),
          dataIndex: "entries",
          render: (_, record) => (
            <DescText
              text={getDescription(record, descriptionProps, tags, rarities, true)}
            />
          ),
          editor: <EntryEditor />,
        },
        getActionColumns<Props>({
          editingData,
          setEditingData,
          width: 200,
          editSubmit: () => tableRef.current?.editSubmit,
          $delete: () => tableRef.current?.delete,
          prefix: (data) => (
            <Button
              color="primary"
              variant="link"
              onClick={() =>
                navigator.clipboard.writeText(JSON.stringify(data, null, 2))
              }
            >
              <CopyOutlined />
            </Button>
          ),
        }),
      ]}
      />
      </PropSelectorFilterContext.Provider>
    </PropNameRefsContext.Provider>
  );
}

interface EditorProps<T> {
  value?: T;
  onChange?: (v: T) => void;
}

const DEFAULT_TARGET: Partial<Target> = { target: "self" };
const DEFAULT_SCOPE: Partial<Scope> = { scope: "neighbor" };
const DEFAULT_CHANCE: Partial<Chance> = { chance: 100 };
const DEFAULT_LOCATION: Partial<Location> = { location: "battle" };
const DEFAULT_TIMES: Partial<Times> = { times: 1 };
const DEFAULT_COUNT: Partial<Count> = { count: 1 };
const DEFAULT_TARGET_SELECT: Partial<TargetSelect> = {
  field: "any",
  limit: 0,
  logic: "or",
  source: "none",
};
const DEFAULT_COMPARE: Partial<TargetSelect> = {
  term: "equal",
  dest: 0,
};
const DEFAULT_YIELD_CHANGE: Partial<YieldChange> = {
  method: "add",
  value: 1,
};
const DEFAULT_WEIGHT_CHANGE: Partial<WeightChange> = {
  raritiesToChange: [1],
  weightMultiplier: 1.1,
};
const DEFAULT_EACH_TURNS_TRIGGER = {
  count: 1,
  loops: 0,
};
function withValue<T>(key: string, value: T | undefined) {
  return value === undefined ? {} : { [key]: value };
}

function sanitizeTargetSelect(value?: Partial<TargetSelect>): TargetSelect {
  const source = (value ?? {}) as Partial<TargetSelect> & {
    props?: string[];
    tags?: number[];
    rarities?: number[];
  };
  const field = value?.field ?? "any";
  const logic = value?.logic ?? "or";
  const compare =
    value?.source && value.source !== "none"
      ? {
          source: value.source,
          ...withValue("term", value.term),
          ...withValue("dest", value.dest),
        }
      : { source: "none" as const };

  switch (field) {
    case "prop":
      return {
        field,
        logic,
        ...withValue("props", source.props),
        ...withValue("limit", value?.limit),
        ...compare,
      } as TargetSelect;
    case "tag":
      return {
        field,
        logic,
        ...withValue("tags", source.tags),
        ...withValue("limit", value?.limit),
        ...compare,
      } as TargetSelect;
    case "rarity":
      return {
        field,
        logic,
        ...withValue("rarities", source.rarities),
        ...withValue("limit", value?.limit),
        ...compare,
      } as TargetSelect;
    case "same":
      return {
        field,
        logic,
        limit: value?.limit ?? 0,
        ...compare,
      } as TargetSelect;
    case "any":
    default:
      return {
        field: "any",
        logic,
        ...withValue("limit", value?.limit),
        ...compare,
      } as TargetSelect;
  }
}

function sanitizeTarget(value?: Partial<Target>): Target {
  const source = (value ?? {}) as Partial<Target> & { scope?: Scope["scope"] };
  const target = value?.target ?? "self";
  if (target !== "select") return { target } as Target;
  return {
    target: "select",
    ...(source.scope ? { scope: source.scope } : {}),
    ...sanitizeTargetSelect(value as Partial<TargetSelect>),
  } as Target;
}

function sanitizeYieldChange(value?: Partial<YieldChange>): YieldChange {
  const source = (value ?? {}) as Partial<YieldChange> & {
    min?: number;
    max?: number;
    value?: number;
  };
  const method = value?.method ?? "add";
  if (method === "random") {
    return {
      method,
      ...withValue("min", source.min),
      ...withValue("max", source.max),
    } as YieldChange;
  }
  return {
    method,
    ...withValue("value", source.value),
  } as YieldChange;
}

function sanitizeWeightChange(value?: Partial<WeightChange>): WeightChange {
  return {
    ...withValue("raritiesToChange", value?.raritiesToChange),
    ...withValue("weightMultiplier", value?.weightMultiplier),
  } as WeightChange;
}

function sanitizeTrigger(value?: Partial<Trigger>): Trigger {
  const source = (value ?? {}) as Partial<Trigger> & {
    count?: number;
    loops?: number;
    scope?: Scope["scope"];
    location?: Location["location"];
  };
  const type = value?.type ?? "EachTurns";
  switch (type) {
    case "EachTurns":
      return {
        type,
        ...withValue("count", source.count),
        ...withValue("loops", source.loops),
      } as Trigger;
    case "RelativeProp":
    case "TargetBeenRemoved":
      return {
        type,
        ...(source.scope ? { scope: source.scope } : {}),
        ...sanitizeTargetSelect(value as Partial<TargetSelect>),
      } as Trigger;
    case "BeenUsed":
      return {
        type,
        ...withValue("count", source.count),
      } as Trigger;
    case "Located":
      return {
        type,
        ...withValue("location", source.location),
      } as Trigger;
    case "AfterPrev":
    case "EachTriggered":
    case "EachRemove":
    case "BeenRemoved":
    case "BeenReplaced":
    default:
      return { type } as Trigger;
  }
}

function sanitizeEntry(value?: Partial<Entry>): Entry {
  const source = (value ?? {}) as Partial<Entry> & {
    chance?: number;
    location?: Location["location"];
    count?: number;
    times?: number;
    scope?: Scope["scope"];
    indexes?: number[];
    keepTag?: boolean;
  };
  const type = value?.type ?? "Remove";
  const trigger = sanitizeTrigger(value?.trigger);

  switch (type) {
    case "Remove":
      return {
        type,
        trigger,
        ...sanitizeTarget(value as Partial<Target>),
        ...withValue("chance", source.chance),
      } as Entry;
    case "Add":
      return {
        type,
        trigger,
        ...withValue("location", source.location),
        ...withValue("chance", source.chance),
        ...sanitizeTargetSelect(value as Partial<TargetSelect>),
      } as Entry;
    case "Replace":
      return {
        type,
        trigger,
        ...sanitizeTarget(value as Partial<Target>),
        ...withValue("chance", source.chance),
        ...sanitizeTargetSelect(value as Partial<TargetSelect>),
        ...withValue("keepTag", source.keepTag),
      } as Entry;
    case "ExtraYield":
      return {
        type,
        trigger,
        ...sanitizeTarget(value as Partial<Target>),
        ...withValue("chance", source.chance),
        ...sanitizeYieldChange(value as Partial<YieldChange>),
      } as Entry;
    case "Grow":
      return {
        type,
        trigger,
        ...sanitizeTarget(value as Partial<Target>),
        ...withValue("chance", source.chance),
        ...withValue("count", source.count),
      } as Entry;
    case "Collect":
      return {
        type,
        trigger,
        ...withValue("times", source.times),
        ...(source.scope ? { scope: source.scope } : {}),
        ...sanitizeTargetSelect(value as Partial<TargetSelect>),
        ...withValue("count", source.count),
      } as Entry;
    case "Gain":
      return { type, trigger } as Entry;
    case "Refine":
      return {
        type,
        trigger,
        ...withValue("count", source.count),
      } as Entry;
    case "Timer":
      return {
        type,
        trigger,
        ...sanitizeTarget(value as Partial<Target>),
        ...withValue("count", source.count),
      } as Entry;
    case "Transfer":
    case "Next":
      return { type, trigger } as Entry;
    case "RemoveEntry":
      return {
        type,
        trigger,
        ...sanitizeTargetSelect(value as Partial<TargetSelect>),
        ...withValue("indexes", source.indexes),
      } as Entry;
    case "DrawWeightChange":
      return {
        type,
        trigger,
        ...sanitizeWeightChange(value as Partial<WeightChange>),
      } as Entry;
    case "Protect":
      return {
        type,
        trigger,
        ...(source.scope ? { scope: source.scope } : {}),
        ...sanitizeTargetSelect(value as Partial<TargetSelect>),
      } as Entry;
    default:
      return { type: "Gain", trigger } as Entry;
  }
}

function sanitizePropsData(value: Props): Props {
  return {
    ...value,
    entries: (value.entries ?? []).map((entry) => sanitizeEntry(entry)),
  };
}

function useDefaultedValue<T extends object>(
  value: T | undefined,
  onChange: ((v: T) => void) | undefined,
  defaults: Partial<T>,
) {
  const merged = useMemo(
    () => ({ ...(defaults as object), ...((value ?? {}) as object) }) as T,
    [defaults, value],
  );

  useEffect(() => {
    if (!onChange) return;
    const keys = Object.keys(defaults) as (keyof T)[];
    if (keys.some((key) => value?.[key] === undefined)) onChange(merged);
  }, [defaults, merged, onChange, value]);

  return merged;
}

const EntryEditor: FC<EditorProps<Entry[]>> = ({
  value,
  onChange,
}: {
  value?: Entry[];
  onChange?: (v: Entry[]) => void;
}) => {
  const form = Form.useFormInstance();

  return (
  <ConfigProvider
    theme={{
      components: {
        Form: {
          itemMarginBottom: 0,
        },
      },
    }}
  >
    <Form.List name="entries">
      {(fields, { add, remove }) => (
        <Flex vertical gap={4}>
          {fields.map((field, index) => (
            <Card
              key={field.name}
              title={
                <Flex justify="space-between" align="center">
                  {`词条 ${index + 1}`}
                  <DeleteOutlined
                    style={{ color: "#f00" }}
                    onClick={() => remove(field.name)}
                  />
                </Flex>
              }
              size="small"
            >
              <Form.Item>
                <Flex gap={10} align="flex-start">
                  <Flex vertical style={{ flex: 1 }}>
                    <Form.Item
                      name={[field.name, "type"]}
                      label="类型"
                      tooltip="词条类型：本词条触发后执行的操作"
                    >
                      <Select
                        options={Object.keys(entryEditorMap).map((k) => ({
                          value: k,
                          label: entryEditorMap[k].label,
                        }))}
                        onChange={(nextType) => {
                          const currentValue = form.getFieldValue([
                            "entries",
                            field.name,
                          ]);
                          form.setFieldValue(
                            ["entries", field.name],
                            { ...(currentValue ?? {}), type: nextType },
                          );
                        }}
                      />
                    </Form.Item>
                    <Form.Item noStyle dependencies={[[field.name, "type"]]}>
                      {({ getFieldValue, setFieldValue }) => {
                        const type = getFieldValue([
                          "entries",
                          field.name,
                          "type",
                        ]);
                        const Editor = entryEditorMap[type]?.ParaEditor;
                        if (Editor)
                          return (
                            <Form.Item name={field.name}>
                              <Editor
                                onChange={(nextValue: Entry) =>
                                  setFieldValue(["entries", field.name], {
                                    ...(getFieldValue(["entries", field.name]) ??
                                      {}),
                                    ...(nextValue ?? {}),
                                  })
                                }
                              />
                            </Form.Item>
                          );
                        else return null;
                      }}
                    </Form.Item>
                  </Flex>
                  <Flex vertical style={{ flex: 1 }}>
                    <Form.Item
                      name={[field.name, "trigger", "type"]}
                      label="触发器类型"
                      tooltip="触发器类型：本词条以何种方式触发"
                    >
                      <Select
                        options={Object.keys(triggerEditorMap).map((k) => ({
                          value: k,
                          label: triggerEditorMap[k].label,
                        }))}
                        onChange={(nextType) => {
                          const currentValue = form.getFieldValue([
                            "entries",
                            field.name,
                            "trigger",
                          ]);
                          form.setFieldValue(
                            ["entries", field.name, "trigger"],
                            { ...(currentValue ?? {}), type: nextType },
                          );
                        }}
                      />
                    </Form.Item>
                    <Form.Item
                      noStyle
                      dependencies={[[field.name, "trigger", "type"]]}
                    >
                      {({ getFieldValue, setFieldValue }) => {
                        const type = getFieldValue([
                          "entries",
                          field.name,
                          "trigger",
                          "type",
                        ]);
                        const Editor = triggerEditorMap[type]?.ParaEditor;
                        if (Editor)
                          return (
                            <Form.Item name={[field.name, "trigger"]}>
                              <Editor
                                onChange={(nextValue: Trigger) =>
                                  setFieldValue(
                                    ["entries", field.name, "trigger"],
                                    {
                                      ...(getFieldValue([
                                        "entries",
                                        field.name,
                                        "trigger",
                                      ]) ?? {}),
                                      ...(nextValue ?? {}),
                                    },
                                  )
                                }
                              />
                            </Form.Item>
                          );
                        else return null;
                      }}
                    </Form.Item>
                  </Flex>
                </Flex>
              </Form.Item>
            </Card>
          ))}
          <Button
            icon={<PlusSquareOutlined />}
            type="dashed"
            style={{ color: "#1677ff" }}
            onClick={() =>
              add({
                type: "Remove",
                target: "self",
                chance: 100,
                trigger: {
                  type: "EachTurns",
                  count: 1,
                  loops: 0,
                },
              })
            }
            block
          >
            添加词条
          </Button>
        </Flex>
      )}
    </Form.List>
  </ConfigProvider>
  );
};

function FieldEditor<T>({
  label,
  tooltip,
  children,
}: EditorProps<T> & {
  label: string;
  tooltip: ReactNode;
  children: ReactNode;
}) {
  return (
    <>
      <Form.Item label={label} tooltip={tooltip}>
        {children}
      </Form.Item>
    </>
  );
}

function SelectEditor<T>({
  label,
  field,
  tooltip,
  options,
  multiple,
  value,
  onChange,
}: EditorProps<T> & {
  label: string;
  field: keyof T;
  tooltip: ReactNode;
  multiple?: boolean;
  options: { value: string; label: string }[];
}) {
  return (
    <FieldEditor label={label} tooltip={tooltip}>
      <Select
        options={options}
        mode={multiple ? "multiple" : undefined}
        value={value?.[field]}
        onChange={(v) => onChange?.({ ...value, [field]: v } as T)}
      />
    </FieldEditor>
  );
}

type NumberKeys<T> = {
  [K in keyof T]: T[K] extends number | undefined ? K : never;
}[keyof T];

function NumberEditor<T, TK extends NumberKeys<T>>({
  label,
  field,
  tooltip,
  precision = 0,
  step = 1,
  min = 0,
  max = 20,
  suffix,
  value,
  onChange,
}: EditorProps<T> & {
  label: string;
  field: TK;
  tooltip: ReactNode;
  precision?: number;
  step?: number;
  suffix?: ReactNode;
  min?: number;
  max?: number;
}) {
  return (
    <FieldEditor label={label} tooltip={tooltip}>
      <InputNumber
        min={min}
        max={max}
        precision={precision}
        step={step}
        suffix={suffix}
        value={value?.[field] as number | undefined}
        onChange={(v) => onChange?.({ ...value, [field]: v } as T)}
      />
    </FieldEditor>
  );
}

const TargetEditor = ({ value, onChange }: EditorProps<Target>) => {
  const defaultedValue = useDefaultedValue(value, onChange, DEFAULT_TARGET);
  const handleChange = (nextValue: Target) =>
    onChange?.({ ...(value ?? {}), ...(nextValue ?? {}) } as Target);

  return (
    <>
    <SelectEditor<Target>
      label="目标"
      field="target"
      tooltip={
        <>
          <p>被执行词条操作的目标</p>
          <p>自身：当前道具本身</p>
          <p>触发者：当前词条触发器中的目标</p>
        </>
      }
      options={[
        { value: "self", label: "自身" },
        { value: "triggers", label: "触发者" },
        { value: "select", label: "筛选" },
      ]}
      value={defaultedValue}
      onChange={handleChange}
    />
    {defaultedValue.target === "select" && (
      <>
        <ScopeEditor
          value={defaultedValue}
          onChange={(v) => handleChange(v as Target)}
        />
        <TargetSelectEditor
          value={defaultedValue}
          onChange={(v) => handleChange(v as Target)}
        />
      </>
    )}
    </>
  );
};

const ScopeEditor = ({ value, onChange }: EditorProps<Scope>) => {
  const defaultedValue = useDefaultedValue(value, onChange, DEFAULT_SCOPE);

  return (
    <SelectEditor<Scope>
    label="范围"
    field="scope"
    tooltip="目标筛选范围"
    options={[
      { value: "neighbor", label: "相邻" },
      { value: "row", label: "同行" },
      { value: "column", label: "同列" },
      { value: "diagonal", label: "对角线上的" },
      { value: "global", label: "全场" },
    ]}
    value={defaultedValue}
    onChange={onChange}
    />
  );
};

const ChanceEditor = ({ value, onChange }: EditorProps<Chance>) => {
  const defaultedValue = useDefaultedValue(value, onChange, DEFAULT_CHANCE);

  return (
    <NumberEditor
    label="概率"
    field="chance"
    tooltip="概率，0%-100%"
    min={0}
    max={100}
    suffix="%"
    value={defaultedValue}
    onChange={onChange}
    />
  );
};

const LocationEditor = ({ value, onChange }: EditorProps<Location>) => {
  const defaultedValue = useDefaultedValue(value, onChange, DEFAULT_LOCATION);

  return (
    <SelectEditor
    label="位置"
    field="location"
    tooltip="指定位置：战场、材料库、角落、空位置"
    options={[
      { value: "battle", label: "战场" },
      { value: "depot", label: "材料库" },
      { value: "corner", label: "角落" },
      { value: "empty", label: "空位置" },
    ]}
    value={defaultedValue}
    onChange={onChange}
    />
  );
};

const TimesEditor = ({ value, onChange }: EditorProps<Times>) => {
  const defaultedValue = useDefaultedValue(value, onChange, DEFAULT_TIMES);

  return (
    <NumberEditor
    label="次数"
    field="times"
    tooltip="次数，0表示无限次"
    min={0}
    max={100}
    value={defaultedValue}
    onChange={onChange}
    />
  );
};

const CountEditor = ({ value, onChange }: EditorProps<Count>) => {
  const defaultedValue = useDefaultedValue(value, onChange, DEFAULT_COUNT);

  return (
    <NumberEditor
    label="数量"
    field="count"
    tooltip="数量，正数为增加，负数为减少"
    min={-100}
    max={100}
    value={defaultedValue}
    onChange={onChange}
    />
  );
};

const IndexesEditor = ({ value, onChange }: EditorProps<Indexes>) => (
  <FieldEditor label="索引" tooltip="索引数组，索引从0开始，空代表全部">
    <Select
      options={[
        { value: 0, label: "0" },
        { value: 1, label: "1" },
        { value: 2, label: "2" },
        { value: 3, label: "3" },
      ]}
      mode="multiple"
      value={value?.indexes}
      onChange={(v) => onChange?.({ ...value, indexes: v } as Indexes)}
    />
  </FieldEditor>
);

const RaritySelector = ({ field, multiple, value, onChange }: any) => {
  const { data: rarities } = useKeyValueData("proprarities");
  return (
    <SelectEditor
      label="品质"
      field={field}
      tooltip="指定目标品质"
      options={rarities as any}
      multiple={multiple}
      value={value}
      onChange={onChange}
    />
  );
};

const PropSelectEditor = ({
  value,
  onChange,
}: EditorProps<TargetSelect>) => {
  const propNameRefs = useContext(PropNameRefsContext);
  const { filter: lastFilter, setFilter: setLastFilter } = useContext(
    PropSelectorFilterContext,
  );
  const { data: tags } = useKeyValueData("proptags");
  const [open, setOpen] = useState(false);
  const [draftFilter, setDraftFilter] =
    useState<PropSelectorFilter>(lastFilter);
  const [draftSelectedIds, setDraftSelectedIds] = useState<string[]>([]);

  const selectablePropNameRefs = useMemo(
    () => propNameRefs.filter((prop) => Number(prop.type) === STOCK_PROP_TYPE),
    [propNameRefs],
  );
  const selectedIds = useMemo(
    () => (value?.field === "prop" ? value.props ?? [] : []),
    [value],
  );
  const selectedIdSet = useMemo(
    () => new Set(draftSelectedIds),
    [draftSelectedIds],
  );
  const draftTagFilterSet = useMemo(
    () => new Set(draftFilter.tagIds),
    [draftFilter.tagIds],
  );
  const propMap = useMemo(
    () => new Map(propNameRefs.map((prop) => [prop._id.toString(), prop])),
    [propNameRefs],
  );
  const matchesTagFilter = useCallback(
    (prop?: PropNameRef) =>
      draftTagFilterSet.size === 0 ||
      Boolean(prop?.tags?.some((tag) => draftTagFilterSet.has(tag))),
    [draftTagFilterSet],
  );
  const matchesKeywordFilter = useCallback(
    (prop: PropNameRef) => {
      const keyword = draftFilter.keyword.trim().toLowerCase();
      if (!keyword) return true;
      return [prop.name, prop._id, prop.assetId]
        .filter(Boolean)
        .some((text) => String(text).toLowerCase().includes(keyword));
    },
    [draftFilter.keyword],
  );
  const filteredPropNameRefs = useMemo(
    () =>
      selectablePropNameRefs.filter(
        (prop) => matchesTagFilter(prop) && matchesKeywordFilter(prop),
      ),
    [matchesKeywordFilter, matchesTagFilter, selectablePropNameRefs],
  );
  const selectedProps = useMemo(
    () =>
      draftSelectedIds
        .map((id) => propMap.get(id))
        .filter((prop): prop is PropNameRef => Boolean(prop)),
    [draftSelectedIds, propMap],
  );
  const selectedText = useMemo(() => {
    if (!selectedIds.length) return "请选择道具";
    return selectedIds.map((id) => propMap.get(id)?.name ?? id).join("、");
  }, [propMap, selectedIds]);

  const resetDraft = useCallback(() => {
    setDraftFilter(lastFilter);
    setDraftSelectedIds(selectedIds);
  }, [lastFilter, selectedIds]);

  const handleOpen = () => {
    resetDraft();
    setOpen(true);
  };

  const handleToggleProp = (propId: string, checked: boolean) => {
    setDraftSelectedIds((prev) => {
      if (checked) return prev.includes(propId) ? prev : [...prev, propId];
      return prev.filter((id) => id !== propId);
    });
  };

  const handleConfirm = () => {
    setLastFilter(draftFilter);
    onChange?.({ ...value, field: "prop", props: draftSelectedIds } as TargetSelect);
    setOpen(false);
  };

  const handleCancel = () => {
    resetDraft();
    setOpen(false);
  };

  const selectorContent = (
    <Flex vertical gap={10}>
      <Input
        allowClear
        placeholder="搜索名称 / ID / 资源ID"
        value={draftFilter.keyword}
        onChange={(e) =>
          setDraftFilter((prev) => ({ ...prev, keyword: e.target.value }))
        }
      />
      <Flex vertical gap={6}>
        <Flex justify="space-between" align="center">
          <span style={{ color: "#666" }}>标签筛选</span>
          <Button
            type="link"
            size="small"
            disabled={
              draftFilter.keyword.length === 0 &&
              draftFilter.tagIds.length === 0
            }
            onClick={() => setDraftFilter({ keyword: "", tagIds: [] })}
          >
            清空筛选
          </Button>
        </Flex>
        <Checkbox.Group
          value={draftFilter.tagIds}
          onChange={(nextValue) =>
            setDraftFilter((prev) => ({
              ...prev,
              tagIds: nextValue as number[],
            }))
          }
        >
          <Flex gap={8} wrap="wrap">
            {tags.length ? (
              tags.map((tag) => (
                <Checkbox key={tag.value} value={tag.value}>
                  {tag.label}
                </Checkbox>
              ))
            ) : (
              <span style={{ color: "#999" }}>暂无标签</span>
            )}
          </Flex>
        </Checkbox.Group>
      </Flex>
        <Flex justify="space-between" align="center">
          <span style={{ color: "#999", fontSize: 12 }}>
            {`显示 ${filteredPropNameRefs.length}/${selectablePropNameRefs.length} 个原料，全部道具 ${propNameRefs.length} 个`}
          </span>
        <Button
          type="link"
          size="small"
          disabled={draftSelectedIds.length === 0}
          onClick={() => setDraftSelectedIds([])}
        >
          清空选择
        </Button>
      </Flex>
      {selectedProps.length > 0 && (
        <Flex gap={4} wrap="wrap">
          {selectedProps.map((prop) => (
            <Tag
              key={prop._id}
              closable
              onClose={(e) => {
                e.preventDefault();
                handleToggleProp(prop._id.toString(), false);
              }}
            >
              {prop.name}
            </Tag>
          ))}
        </Flex>
      )}
      <div
        style={{
          maxHeight: 300,
          overflowY: "auto",
          border: "1px solid #f0f0f0",
          borderRadius: 6,
        }}
      >
        {filteredPropNameRefs.length ? (
          filteredPropNameRefs.map((prop) => {
            const propId = prop._id.toString();
            return (
              <Flex
                key={propId}
                align="center"
                justify="space-between"
                gap={8}
                style={{
                  padding: "7px 10px",
                  borderBottom: "1px solid #f5f5f5",
                }}
              >
                <Checkbox
                  checked={selectedIdSet.has(propId)}
                  onChange={(e) => handleToggleProp(propId, e.target.checked)}
                >
                  {prop.name}
                </Checkbox>
                <Flex gap={4} wrap="wrap" justify="flex-end">
                  {prop.tags?.slice(0, 3).map((tagId) => (
                    <Tag key={tagId} style={{ marginInlineEnd: 0 }}>
                      {tags.find((tag) => tag.value === tagId)?.label ?? tagId}
                    </Tag>
                  ))}
                </Flex>
              </Flex>
            );
          })
        ) : (
          <div style={{ padding: 20, color: "#999", textAlign: "center" }}>
            没有符合条件的道具
          </div>
        )}
      </div>
    </Flex>
  );

  return (
    <FieldEditor label="道具" tooltip="指定目标道具">
      <Button
        block
        htmlType="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleOpen();
        }}
        style={{
          height: "auto",
          minHeight: 32,
          whiteSpace: "normal",
          textAlign: "left",
        }}
      >
        <span
          style={{
            color: selectedIds.length ? undefined : "#999",
            wordBreak: "break-all",
          }}
        >
          {selectedText}
        </span>
      </Button>
      <Modal
        open={open}
        title="选择道具"
        width={540}
        okText="确认"
        cancelText="取消"
        onOk={handleConfirm}
        onCancel={handleCancel}
      >
        {selectorContent}
      </Modal>
    </FieldEditor>
  );
};

const TargetSelectEditor = ({ value, onChange }: EditorProps<TargetSelect>) => {
  const { data: tags } = useKeyValueData("proptags");
  const defaultedValue = useDefaultedValue(
    value,
    onChange,
    DEFAULT_TARGET_SELECT,
  );
  const compareValue =
    defaultedValue.source && defaultedValue.source !== "none"
      ? ({
          ...DEFAULT_COMPARE,
          ...defaultedValue,
        } as TargetSelect)
      : defaultedValue;
  const handleChange = (nextValue: TargetSelect) =>
    onChange?.({ ...(value ?? {}), ...(nextValue ?? {}) } as TargetSelect);

  useEffect(() => {
    if (!onChange) return;
    if (
      defaultedValue.source &&
      defaultedValue.source !== "none" &&
      (defaultedValue.term === undefined || defaultedValue.dest === undefined)
    ) {
      handleChange(compareValue);
    }
  }, [compareValue, defaultedValue, handleChange]);

  return (
    <>
      <SelectEditor
        label="字段"
        field="field"
        tooltip="用于筛选目标的字段：指定道具、标签、品质"
        options={[
          { value: "prop", label: "指定道具" },
          { value: "tag", label: "标签" },
          { value: "rarity", label: "品质" },
          { value: "any", label: "任意" },
          { value: "same", label: "相同" },
        ]}
        value={defaultedValue}
        onChange={handleChange}
      />
      {defaultedValue.field === "prop" && (
        <PropSelectEditor value={defaultedValue} onChange={handleChange} />
      )}
      {defaultedValue.field === "tag" && (
        <SelectEditor
          label="标签"
          field="tags"
          tooltip="指定目标标签"
          options={tags as any}
          multiple
          value={defaultedValue}
          onChange={handleChange}
        />
      )}
      {defaultedValue.field === "rarity" && (
        <RaritySelector
          field="rarities"
          multiple
          value={defaultedValue}
          onChange={handleChange}
        />
      )}
      {["prop", "tag", "rarity"].includes(defaultedValue.field) && (
        <SelectEditor
          label="逻辑"
          field="logic"
          tooltip="多个筛选值之间的关系"
          options={[
            { value: "or", label: "或" },
            { value: "and", label: "和" },
          ]}
          value={defaultedValue}
          onChange={handleChange}
        />
      )}
      <NumberEditor
        label="数量"
        field="limit"
        tooltip={
          <>
            <p>指定筛选时满足条件的数量，或添加数量</p>
            <p>0：表示任意数量/不限制数量/所有符合条件的目标</p>
            <p>-1：反向查找，表示未筛选到目标时触发</p>
          </>
        }
        min={-1}
        value={defaultedValue}
        onChange={handleChange}
      />
      <SelectEditor
        label="取值"
        field="source"
        tooltip="待比较的数值来源"
        options={[
          { value: "none", label: "无" },
          { value: "yield", label: "产出" },
        ]}
        value={defaultedValue}
        onChange={handleChange}
      />
      {defaultedValue.source && defaultedValue.source !== "none" && (
        <>
          <SelectEditor
            label="比较"
            field="term"
            tooltip="比较方式"
            options={[
              { value: "equal", label: "等于" },
              { value: "greaterOrEqual", label: "大于等于" },
              { value: "lessOrEqual", label: "小于等于" },
              { value: "greater", label: "大于" },
              { value: "less", label: "小于" },
            ]}
            value={compareValue}
            onChange={handleChange}
          />
          <NumberEditor
            label="值"
            field="dest"
            tooltip="目标值"
            value={compareValue}
            onChange={handleChange}
          />
        </>
      )}
    </>
  );
};

const YieldChangeEditor = ({ value, onChange }: EditorProps<YieldChange>) => {
  const defaultedValue = useDefaultedValue(
    value,
    onChange,
    DEFAULT_YIELD_CHANGE,
  );
  const handleChange = (nextValue: YieldChange) =>
    onChange?.({ ...(value ?? {}), ...(nextValue ?? {}) } as YieldChange);

  return (
    <>
      <SelectEditor
        label="方式"
        field="method"
        tooltip="计算方式"
        options={[
          { value: "add", label: "增加" },
          { value: "multiply", label: "倍率" },
          { value: "random", label: "随机" },
        ]}
        value={defaultedValue}
        onChange={handleChange}
      />
      {defaultedValue.method === "add" && (
        <NumberEditor
          label="数量"
          field="value"
          tooltip="增加固定数量产出"
          min={-100}
          max={100}
          value={defaultedValue}
          onChange={handleChange}
        />
      )}
      {defaultedValue.method === "multiply" && (
        <NumberEditor
          label="倍率"
          field="value"
          tooltip="按照倍率计算产出"
          precision={2}
          min={0.01}
          max={100}
          value={defaultedValue}
          onChange={handleChange}
        />
      )}
      {defaultedValue.method === "random" && (
        <>
          <NumberEditor
            label="最小"
            field="min"
            tooltip="随机产出最小值（包含）"
            min={-100}
            max={100}
            value={defaultedValue}
            onChange={handleChange}
          />
          <NumberEditor
            label="最大"
            field="max"
            tooltip="随机产出最大值（包含）"
            min={-100}
            max={100}
            value={defaultedValue}
            onChange={handleChange}
          />
        </>
      )}
    </>
  );
};

const WeightChangeEditor = ({ value, onChange }: EditorProps<WeightChange>) => {
  const defaultedValue = useDefaultedValue(
    value,
    onChange,
    DEFAULT_WEIGHT_CHANGE,
  );
  const handleChange = (nextValue: WeightChange) =>
    onChange?.({ ...(value ?? {}), ...(nextValue ?? {}) } as WeightChange);

  return (
    <>
      <RaritySelector
        field="raritiesToChange"
        multiple
        value={defaultedValue}
        onChange={handleChange}
      />
      <NumberEditor
        label="倍率"
        field="weightMultiplier"
        tooltip="目标品质抽取权重倍率"
        precision={1}
        step={0.1}
        min={0}
        max={10}
        value={defaultedValue}
        onChange={handleChange}
      />
    </>
  );
};

const entryEditorMap: Record<
  string,
  {
    label: string;
    ParaEditor?: FC<EditorProps<any>>;
  }
> = {
  Remove: {
    label: "消除",
    ParaEditor: ({ value, onChange }) => (
      <>
        <TargetEditor value={value} onChange={onChange} />
        <ChanceEditor value={value} onChange={onChange} />
      </>
    ),
  },
  Add: {
    label: "添加",
    ParaEditor: ({ value, onChange }) => (
      <>
        <LocationEditor value={value} onChange={onChange} />
        <ChanceEditor value={value} onChange={onChange} />
        <TargetSelectEditor value={value} onChange={onChange} />
      </>
    ),
  },
  Replace: {
    label: "转化",
    ParaEditor: ({ value, onChange }) => (
      <>
        <TargetEditor value={value} onChange={onChange} />
        <ChanceEditor value={value} onChange={onChange} />
        <TargetSelectEditor value={value} onChange={onChange} />
        <FieldEditor label="保留标签" tooltip="转化时保留原道具标签">
          <Checkbox
            checked={value?.keepTag}
            onChange={(e) =>
              onChange?.({ ...value, keepTag: e.target.checked })
            }
          />
        </FieldEditor>
      </>
    ),
  },
  ExtraYield: {
    label: "额外产出",
    ParaEditor: ({ value, onChange }) => (
      <>
        <TargetEditor value={value} onChange={onChange} />
        <ChanceEditor value={value} onChange={onChange} />
        <YieldChangeEditor value={value} onChange={onChange} />
      </>
    ),
  },
  Grow: {
    label: "永久增加产出",
    ParaEditor: ({ value, onChange }) => (
      <>
        <TargetEditor value={value} onChange={onChange} />
        <ChanceEditor value={value} onChange={onChange} />
        <CountEditor value={value} onChange={onChange} />
      </>
    ),
  },
  Collect: {
    label: "收集",
    ParaEditor: ({ value, onChange }) => (
      <>
        <TimesEditor value={value} onChange={onChange} />
        <ScopeEditor value={value} onChange={onChange} />
        <TargetSelectEditor value={value} onChange={onChange} />
        <CountEditor value={value} onChange={onChange} />
      </>
    ),
  },
  Gain: {
    label: "收获",
    ParaEditor: ({ value, onChange }) => <></>,
  },
  Refine: {
    label: "调和次数",
    ParaEditor: ({ value, onChange }) => (
      <CountEditor value={value} onChange={onChange} />
    ),
  },
  Timer: {
    label: "倒计时",
    ParaEditor: ({ value, onChange }) => (
      <>
        <TargetEditor value={value} onChange={onChange} />
        <CountEditor value={value} onChange={onChange} />
      </>
    ),
  },
  Transfer: {
    label: "转移位置",
    ParaEditor: ({ value, onChange }) => <></>,
  },
  Next: {
    label: "下一词条",
    ParaEditor: ({ value, onChange }) => <></>,
  },
  RemoveEntry: {
    label: "消除特性",
    ParaEditor: ({ value, onChange }) => (
      <>
        <TargetSelectEditor value={value} onChange={onChange} />
        <IndexesEditor value={value} onChange={onChange} />
      </>
    ),
  },
  DrawWeightChange: {
    label: "品质权重调整",
    ParaEditor: ({ value, onChange }) => (
      <WeightChangeEditor value={value} onChange={onChange} />
    ),
  },
  Protect: {
    label: "保护",
    ParaEditor: ({ value, onChange }) => (
      <>
        <ScopeEditor value={value} onChange={onChange} />
        <TargetSelectEditor value={value} onChange={onChange} />
      </>
    ),
  },
};

const triggerEditorMap: Record<
  string,
  {
    label: string;
    ParaEditor?: FC<EditorProps<any>>;
  }
> = {
  EachTurns: {
    label: "每X回合",
    ParaEditor: ({ value, onChange }) => (
      <>
        <CountEditor value={value} onChange={onChange} />
        <NumberEditor
          label="循环"
          field="loops"
          tooltip="循环次数，0表示无限循环"
          min={0}
          max={100}
          value={value}
          onChange={onChange}
        />
      </>
    ),
  },
  AfterPrev: {
    label: "上一条后",
  },
  EachTriggered: {
    label: "上一词条每次触发时",
  },
  EachRemove: {
    label: "消除时",
  },
  BeenRemoved: {
    label: "被消除时",
  },
  BeenReplaced: {
    label: "被转化时",
  },
  RelativeProp: {
    label: "查找目标",
    ParaEditor: ({ value, onChange }) => (
      <>
        <ScopeEditor value={value} onChange={onChange} />
        <TargetSelectEditor value={value} onChange={onChange} />
      </>
    ),
  },
  TargetBeenRemoved: {
    label: "目标被消除时",
    ParaEditor: ({ value, onChange }) => (
      <>
        <ScopeEditor value={value} onChange={onChange} />
        <TargetSelectEditor value={value} onChange={onChange} />
      </>
    ),
  },
  BeenUsed: {
    label: "使用后",
    ParaEditor: CountEditor,
  },
  Located: {
    label: "位于位置",
    ParaEditor: LocationEditor,
  },
};
